import React, { useState, useEffect } from "react";
import Question from "../components/Question";
import ClientForm from "../components/ui/ClientForm";
import { getQuestions, savePlayerInfo, savePlayerResult } from "../services/GameSvc";

const gameAudio = new Audio("/assets/sounds/game-screen.mp3");

const Game = ({ onGameEnd, restart }) => {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [score, setScore] = useState(0);
  const [view, setView] = useState(restart === "retry" ? "game" : "start");
  const [showClientForm, setShowClientForm] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [playerId, setPlayerId] = useState(null);
  // Restore playerId from localStorage only for retry; clear otherwise
  useEffect(() => {
  if (restart === "retry") {
      const savedId = localStorage.getItem("playerId");
      if (savedId) {
        setPlayerId(parseInt(savedId));
      }
    }
  }, [restart]);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await getQuestions();
        const fetchedQuestions = response;

        // Map backend fields to shape expected by UI
        const formattedQuestions = fetchedQuestions.map(q => ({
          ...q,
          options: [
            { id: "1", text: q.option1 },
            { id: "2", text: q.option2 },
            { id: "3", text: q.option3 },
            { id: "4", text: q.option4 },
          ],
          correctAnswer: q.correct_option
        }));

        // Fisher-Yates shuffle (unbiased).
        const shuffleArray = (array) => {
          const copy = [...array];
          for (let i = copy.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [copy[i], copy[j]] = [copy[j], copy[i]];
          }
          return copy;
        };

        const selected = shuffleArray(formattedQuestions)
          .slice(0, 5)
          .map((q) => ({ ...q, options: shuffleArray(q.options) }));

        setQuestions(selected);
        setCurrentIndex(0);
        setSelectedOption(null);
        setScore(0);
        setAnswers([]);

      } catch (error) {
        console.error("Failed to fetch questions:", error);
      }
    };

    if (view === "start") {
      gameAudio.pause();
      gameAudio.currentTime = 0;
    } else if (view === "game") {
      gameAudio.loop = true;
      gameAudio.play().catch(() => {});
    }

    if (view === "game") {
      fetchQuestions();
    }
  }, [restart, clientData, playerId, view]);

  // Stop the background music when leaving the game (e.g. to the score screen).
  useEffect(() => {
    return () => {
      gameAudio.pause();
      gameAudio.currentTime = 0;
    };
  }, []);

  const startGame = () => {
    if (clientData || playerId) {
      setView("game");
    } else {
      setShowClientForm(true);
    }
  };

  const handleClientFormSubmit = async (data) => {
    try {
      const result = await savePlayerInfo(data);
      console.log("Returned from savePlayerInfo:", result);

      if (result && result.player_id) {
        localStorage.setItem("playerId", result.player_id);
        data.id = result.player_id;
        setPlayerId(result.player_id);
        setClientData(data);
        setShowClientForm(false);
        setView("game");
      } else {
        console.error("No player ID returned:", result);
        alert("Erro ao recuperar o ID do jogador.");
      }
    } catch (error) {
      console.error("Erro ao enviar os dados do jogador:", error);
      alert("Erro na comunicação com o servidor.");
    }
  };

  const handleOptionClick = (optionId, elapsedMs = 0) => {
    if (!questions[currentIndex]) return;
    if (selectedOption) return;

    const totalSeconds = elapsedMs / 1000;
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const seconds = String(Math.floor(totalSeconds % 60)).padStart(2, '0');
    const milliseconds = String(Math.round(elapsedMs % 1000)).padStart(3, '0');
    const timeTaken = `${minutes}:${seconds}:${milliseconds}`;

    const correct = questions[currentIndex].correctAnswer;
    // Option ids are strings while correct_option is numeric: compare as strings.
    const isCorrect = String(optionId) === String(correct) ? 1 : 0;

    setSelectedOption(optionId);
    setScore(prev => prev + isCorrect);

    const newAnswer = {
      questionId: parseInt(questions[currentIndex].id),
      selectedOption: parseInt(optionId),
      timeTaken,
      correct: isCorrect,
    };

    const composedAnswers = [...answers, newAnswer];
    setAnswers(composedAnswers);

    setTimeout(() => {
      if (currentIndex + 1 < questions.length) {
        setCurrentIndex(currentIndex + 1);
        setSelectedOption(null);
      } else {
        const finalScore = score + isCorrect;

        savePlayerResult({
          player_id: playerId,
          result: composedAnswers,
        }).then(() => {
          setAnswers([]); // Clear AFTER save
          setQuestions([]); // Reset AFTER save
          onGameEnd(finalScore, composedAnswers.length);
        }).catch(err => {
          console.error("Error saving result:", err);
          alert("Ocorreu um erro ao guardar os resultados.");
        });
      }
    }, 1500);
  };

  if (!questions.length && view === "game") return <div>Loading...</div>;

  return (
    <div className="app">
      <div className="bg">
        <div className="bg-panel"></div>
        <div className="bg-panel"></div>
        <div className="bg-panel"></div>
      </div>
      <div className="logo-wrapper">
          <div className="logo"><img src="/assets/images/reciclar_esta_na_moda.png" alt="Juntos Fazemos o Impossível" /></div>
          <div className="logo quiz"><img src="/assets/images/quiz_logo.png" alt="Quiz" /></div>
          <div className="logo-group">
              <div className="logo"><img src="/assets/images/valorsul_white_logo.png" alt="Valorsul" /></div>
              <div className="logo"><img src="/assets/images/linha_da_reciclagem_logo_w.png" alt="Linha da Reciclagem" /></div>
              <div className="logo"><img src="/assets/images/simar_logo_w.png" alt="SIMAR" /></div>
          </div>
      </div>

      {view === "start" && (
        <div className="start-screen">
          <button className="start-btn" onClick={startGame}>Começar</button>
          {showClientForm && (
            <div className="start-modal">
              <div className="modal-content">
                <h2>Inscrição</h2>
                <p>Insira os seus dados para jogar</p>
                <ClientForm onSubmit={handleClientFormSubmit} />
              </div>
            </div>
          )}
        </div>
      )}

      {view === "game" && (
        <div className="game-screen">
          <Question
            data={questions[currentIndex]}
            selectedOption={selectedOption}
            onOptionClick={handleOptionClick}
          />
        </div>
      )}
    </div>
  );
};

export default Game;
